import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Switch,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { alpha, styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../components/common/GradientCircularProgress';
import BorderedButton from '../../../components/common/buttons/BorderedButton';
import PrimaryBlueButton from '../../../components/common/buttons/PrimaryBlueButton';
import useBackendApi from '../../../hooks/useBackendApi';
import { useThemeContext } from '../../../contexts/ThemeContext.js';
import { colors } from '../../../styles/fontStyles';
import { formatDate } from '../../../utils';
import { formatDateTime } from '../../../utils/dateTime';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type OrderDetails = {
  order_id?: string | null;
  name?: string | null;
  date?: string | null;
  total_quantity?: number | null;
  fulfillment_status?: string | null;
  product_name?: string | null;
  total_paid?: string | number | null;
  cost?: {
    total_amount?: {
      amount?: string | number;
    };
  } | null;
};

type CustomerDetails = {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
};

type AddressDetails = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
};

type TestKitDetails = {
  test_kit_id: number;
  subject_name?: string | null;
  sample_id?: string | null;
  activated?: boolean | null;
  activated_at?: string | null;
  outbound_tracking_company?: string | null;
  outbound_tracking_number?: string | null;
  outbound_tracking_url?: string | null;
  outbound_label_url?: string | null;
  outbound_vendor?: string | null;
  inbound_tracking_company?: string | null;
  inbound_tracking_number?: string | null;
  inbound_tracking_url?: string | null;
  inbound_label_url?: string | null;
  inbound_vendor?: string | null;
  kit_ready?: boolean | null;
};

type OrderDetailsResponse = {
  order: OrderDetails;
  customer: CustomerDetails;
  address: AddressDetails;
  test_kits: TestKitDetails[];
};

type UpdateSampleIdResponse = {
  sample_id?: string | null;
};

type RouteParams = {
  org_path_name: string;
  order_id: string;
};

type StatusPillProps = {
  label: string;
  isActive: boolean;
  textColor: string;
};

type OrderDetailsSectionCardProps = {
  title?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

type ShippingLeg = {
  key: 'outbound' | 'inbound';
  label: 'Outbound' | 'Inbound';
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  labelUrl?: string | null;
  vendor?: string | null;
};

type OrderDetailsOrgTestKitsSectionProps = {
  testKits: TestKitDetails[];
  editingTestKitId: number | null;
  sampleIdDraft: string;
  savingTestKitId: number | null;
  togglingKitReadyId: number | null;
  testKitSaveError: string | null;
  testKitSuccessMessage: string | null;
  onCloseSuccess: () => void;
  onStartEditingTestKit: (testKit: TestKitDetails) => void;
  onCancelEditingTestKit: () => void;
  onChangeSampleIdDraft: (value: string) => void;
  onSaveTestKitSampleId: (testKitId: number) => void;
  onToggleKitReady: (testKitId: number, isChecked: boolean) => void;
};

const ShippingLabel = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  lineHeight: 1.2,
});

const TrackingLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  fontSize: '0.95rem',
  fontWeight: 600,
  lineHeight: 1.2,
  color: colors.primaryBlue,
  textDecoration: 'none',
  textUnderlineOffset: '2px',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
  },
});

function OrderDetailsSectionCard({ title, children, sx }: OrderDetailsSectionCardProps): JSX.Element {
  const { theme } = useThemeContext();

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
        borderRadius: '16px',
        padding: { xs: '16px', sm: '22px' },
        border: '1px solid',
        borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
        ...sx,
      }}
    >
      {title ? (
        <>
          <Typography
            sx={{
              mb: 1,
              color: theme.custom.pages.frost.authHome.typography.body1.color,
              textAlign: 'left',
              fontSize: '1.2rem',
              fontWeight: 400,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              borderBottom: '1px solid',
              borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
              mb: 2,
            }}
          />
        </>
      ) : null}
      {children}
    </Box>
  );
}

function StatusPill({ label, isActive, textColor }: StatusPillProps): JSX.Element {
  return (
    <Box
      sx={{
        textTransform: 'capitalize',
        backgroundColor: isActive ? colors.blue350 : colors.gray150,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        display: 'inline-block',
        color: textColor,
      }}
    >
      {label}
    </Box>
  );
}

function OrderDetailsOrgTestKitsSection({
  testKits,
  editingTestKitId,
  sampleIdDraft,
  savingTestKitId,
  togglingKitReadyId,
  testKitSaveError,
  testKitSuccessMessage,
  onCloseSuccess,
  onStartEditingTestKit,
  onCancelEditingTestKit,
  onChangeSampleIdDraft,
  onSaveTestKitSampleId,
  onToggleKitReady,
}: OrderDetailsOrgTestKitsSectionProps): JSX.Element {
  const { theme } = useThemeContext();

  const sectionBorderColor = theme.custom.pages.frost.authHome.cards.borderColor;
  const sectionHeadingColor = theme.custom.pages.frost.authHome.typography.body1.color;
  const sectionLabelColor = theme.custom.pages.frost.authHome.typography.body2.color;
  const sectionValueColor = theme.custom.pages.frost.authHome.typography.body1.color;
  const tableHeaderBackgroundColor =
    theme.palette.mode === 'dark'
      ? theme.custom.report.details.resultTable.styledHeaderCell.backgroundColor
      : theme.custom.pages.frost.authHome.cards.event.backgroundColor;
  const stripedRowBackgroundColor =
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.black, 0.018);
  const stripedRowHoverBackgroundColor =
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.035);

  const tableContainerSx = {
    borderRadius: '18px',
    border: '1px solid',
    borderColor: sectionBorderColor,
    overflow: 'hidden',
    backgroundColor: theme.layout.defaults.backgroundColor,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : theme.shadows[1],
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    '& .MuiTableCell-head': {
      backgroundColor: tableHeaderBackgroundColor,
      fontWeight: 800,
      color: sectionHeadingColor,
      fontSize: '1rem',
      lineHeight: 1.2,
      padding: '18px 18px',
      borderBottom: `1px solid ${sectionBorderColor}`,
      borderRight: `1px solid ${sectionBorderColor}`,
      whiteSpace: 'normal',
      '&:last-of-type': {
        borderRight: 0,
      },
    },
    '& .MuiTableCell-body': {
      padding: '20px 18px',
      borderBottom: `1px solid ${sectionBorderColor}`,
    },
  };
  const tableBodyCellSx = {
    verticalAlign: 'top',
  };
  const tableBodyCellWithDividerSx = {
    ...tableBodyCellSx,
    borderRight: `1px solid ${sectionBorderColor}`,
  };
  const subLabelSx = {
    display: 'block',
    color: sectionLabelColor,
    fontSize: '0.8rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    mb: 0.25,
    lineHeight: 1.2,
  };
  const cellValueSx = {
    color: sectionValueColor,
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.25,
  };
  const actionLinkSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    color: colors.primaryBlue,
    fontWeight: 500,
    fontSize: '0.9rem',
    lineHeight: 1.2,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  };

  const renderShippingLeg = (shippingLeg: ShippingLeg): JSX.Element => {
    const downloadTooltip = shippingLeg.vendor
      ? `Download ${shippingLeg.label} Label (${shippingLeg.vendor})`
      : `Download ${shippingLeg.label} Label`;
      
    const displayLabel = shippingLeg.label === 'Outbound' ? 'To Customer' : 'To Lab';

    return (
      <Box key={shippingLeg.key} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, py: 0.5 }}>
        <ShippingLabel sx={{ color: sectionLabelColor }}>{displayLabel}:</ShippingLabel>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {shippingLeg.labelUrl ? (
            <Tooltip title={downloadTooltip}>
              <Link
                href={shippingLeg.labelUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Download ${shippingLeg.key} label`}
                sx={{
                  color: colors.primaryBlue,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Print ⬇️
              </Link>
            </Tooltip>
          ) : null}

          {shippingLeg.trackingNumber ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {shippingLeg.trackingUrl ? (
                <TrackingLink
                  href={shippingLeg.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ minWidth: 0, fontWeight: 500 }}
                >
                  Tracking
                </TrackingLink>
              ) : (
                <Typography sx={{ ...cellValueSx, fontSize: '0.95rem', fontWeight: 500 }}>
                  Tracking
                </Typography>
              )}
              <Tooltip title="Copy Tracking ID">
                <Link
                  component="button"
                  onClick={() => navigator.clipboard.writeText(shippingLeg.trackingNumber || '')}
                  sx={{
                    color: colors.primaryBlue,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'none' }
                  }}
                >
                  📋
                </Link>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  return (
    <OrderDetailsSectionCard title="Test Kits" sx={{ gridColumn: { lg: '1 / -1' } }}>
      {testKitSuccessMessage ? (
        <Alert severity="success" onClose={onCloseSuccess} sx={{ mb: 3 }}>
          {testKitSuccessMessage}
        </Alert>
      ) : null}

      {testKitSaveError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {testKitSaveError}
        </Alert>
      ) : null}

      {testKits.length === 0 ? (
        <Typography sx={{ color: sectionValueColor }}>No test kits found for this order.</Typography>
      ) : (
        <TableContainer sx={tableContainerSx}>
          <Table aria-label="test kits table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>ID</TableCell>
                <TableCell sx={{ width: '45%' }}>Details</TableCell>
                <TableCell sx={{ width: '40%' }}>Kit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testKits.map((testKit, index) => {
                const isEditing = editingTestKitId === testKit.test_kit_id;
                const isSaving = savingTestKitId === testKit.test_kit_id;
                const rowBackgroundColor = index % 2 === 1 ? stripedRowBackgroundColor : 'transparent';
                const shippingLegs: ShippingLeg[] = [
                  {
                    key: 'outbound',
                    label: 'Outbound',
                    trackingNumber: testKit.outbound_tracking_number,
                    trackingUrl: testKit.outbound_tracking_url,
                    labelUrl: testKit.outbound_label_url,
                    vendor: testKit.outbound_vendor,
                  },
                  {
                    key: 'inbound',
                    label: 'Inbound',
                    trackingNumber: testKit.inbound_tracking_number,
                    trackingUrl: testKit.inbound_tracking_url,
                    labelUrl: testKit.inbound_label_url,
                    vendor: testKit.inbound_vendor,
                  },
                ];

                return (
                  <TableRow
                    key={testKit.test_kit_id}
                    sx={{
                      backgroundColor: rowBackgroundColor,
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                      '&:hover': {
                        backgroundColor:
                          rowBackgroundColor === 'transparent'
                            ? stripedRowBackgroundColor
                            : stripedRowHoverBackgroundColor,
                      },
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell component="th" scope="row" sx={tableBodyCellWithDividerSx}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          color: sectionHeadingColor,
                          lineHeight: 1,
                          mb: 1.1,
                          fontSize: { xs: '1rem', md: '1.2rem' },
                          letterSpacing: '-0.02em',
                        }}
                      >
                        #{testKit.test_kit_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableBodyCellWithDividerSx}>
                      <Box sx={{ mb: 1.15 }}>
                        <Typography sx={subLabelSx}>Sample ID</Typography>
                        {isEditing ? (
                          <Stack spacing={0.85} sx={{ minWidth: 0, width: '100%' }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={sampleIdDraft}
                              onChange={(e) => onChangeSampleIdDraft(e.target.value)}
                              disabled={isSaving}
                              placeholder="Sample ID"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <PrimaryBlueButton
                                onClick={() => onSaveTestKitSampleId(testKit.test_kit_id)}
                                buttonText="Save"
                                isLoading={isSaving}
                                sxProps={{ height: '36px', width: '96px', px: 2 }}
                              />
                              <BorderedButton
                                onClick={onCancelEditingTestKit}
                                buttonText="Cancel"
                                sxProps={{ height: '36px', width: '96px', px: 2 }}
                              />
                            </Box>
                          </Stack>
                        ) : (
                          <Stack spacing={0.45} alignItems="flex-start" sx={{ minWidth: 0, width: '100%' }}>
                            <Typography
                              sx={{
                                color: sectionValueColor,
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                fontSize: '0.95rem',
                                lineHeight: 1.3,
                                display: 'block',
                                maxWidth: '100%',
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {testKit.sample_id || 'N/A'}
                            </Typography>
                            <Link component="button" onClick={() => onStartEditingTestKit(testKit)} sx={actionLinkSx}>
                              [Edit]
                            </Link>
                          </Stack>
                        )}
                      </Box>
                      <Divider sx={{ my: 1, borderColor: sectionBorderColor }} />
                      <Box sx={{ mb: 1.15 }}>
                        <Typography sx={subLabelSx}>Subject</Typography>
                        <Typography sx={{ ...cellValueSx, fontSize: '1rem', fontWeight: 600 }}>
                          {testKit.subject_name || 'N/A'}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1, borderColor: sectionBorderColor }} />
                      <Box>
                        <Typography sx={subLabelSx}>Activated</Typography>
                        {testKit.activated === null || testKit.activated === undefined ? null : (
                          <Typography sx={{ ...cellValueSx, fontSize: '1rem', fontWeight: 600 }}>
                            {testKit.activated ? 'Yes' : 'No'}
                          </Typography>
                        )}
                        {testKit.activated_at ? (
                          <Typography variant="caption" sx={{ color: sectionLabelColor, mt: 0.35, display: 'block' }}>
                            {formatDateTime(testKit.activated_at)}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Typography sx={{ mb: 1, fontWeight: 600, color: sectionValueColor }}>
                        Status: {testKit.kit_ready ? 'Kit Ready' : 'Unfulfilled'}
                      </Typography>
                      <Stack spacing={0.35}>
                        {shippingLegs.map((shippingLeg, index) => (
                          <Box key={shippingLeg.key}>
                            {renderShippingLeg(shippingLeg)}
                            {index < shippingLegs.length - 1 ? (
                              <Divider sx={{ my: 0.75, borderColor: sectionBorderColor }} />
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 1.5, borderColor: sectionBorderColor }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: sectionValueColor }}>Kit Ready:</Typography>
                        <Switch
                          size="small"
                          checked={!!testKit.kit_ready}
                          disabled={!!testKit.kit_ready || !!togglingKitReadyId}
                          onChange={(e) => {
                            e.preventDefault();
                            if (!testKit.kit_ready && togglingKitReadyId !== testKit.test_kit_id) {
                              onToggleKitReady(testKit.test_kit_id, true);
                            }
                          }}
                        />
                        {togglingKitReadyId === testKit.test_kit_id && <CircularProgress size={16} thickness={5} />}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </OrderDetailsSectionCard>
  );
}

export default function OrderDetailsOrg(): JSX.Element {
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();
  const navigate = useNavigate();
  const { org_path_name, order_id } = useParams<RouteParams>();
  const orderId = order_id?.trim() || '';

  const [data, setData] = useState<OrderDetailsResponse | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [notFoundText, setNotFoundText] = useState<string | null>(null);
  const [editingTestKitId, setEditingTestKitId] = useState<number | null>(null);
  const [sampleIdDraft, setSampleIdDraft] = useState<string>('');
  const [savingTestKitId, setSavingTestKitId] = useState<number | null>(null);
  const [testKitSaveError, setTestKitSaveError] = useState<string | null>(null);
  const [testKitSuccessMessage, setTestKitSuccessMessage] = useState<string | null>(null);
  const [togglingKitReadyId, setTogglingKitReadyId] = useState<number | null>(null);

  const sectionLabelColor = theme.custom.pages.frost.authHome.typography.body2.color;
  const sectionValueColor = theme.custom.pages.frost.authHome.typography.body1.color;

  const fail = useCallback((message = 'Unable to load order details. Please try again.') => {
    setData(null);
    setNotFoundText(null);
    setErrorText(message);
  }, []);

  const fetchOrderDetails = useCallback(async () => {
    if (!org_path_name || !orderId) {
      navigate('/404.html');
      return;
    }

    setData(null);
    setErrorText(null);
    setNotFoundText(null);
    setEditingTestKitId(null);
    setSampleIdDraft('');
    setSavingTestKitId(null);
    setTestKitSaveError(null);
    setTestKitSuccessMessage(null);

    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(org_path_name)}/orders/${encodeURIComponent(orderId)}`;
      const res = await apiFetchRequest({ endpoint, method: 'GET' });

      const { status, body } = res;
      if (status === 401 || status === 403) {
        navigate('/401.html');
        return;
      }

      if (status === 404) {
        setData(null);
        setErrorText(null);
        setNotFoundText('Order not found.');
        return;
      }

      if (status !== 200) {
        fail();
        return;
      }

      const typedBody = body as OrderDetailsResponse;

      setData({
        order: typedBody.order,
        customer: typedBody.customer,
        address: typedBody.address,
        test_kits: typedBody.test_kits,
      });
    } catch {
      fail('Something went wrong. Please try again.');
    }
  }, [apiFetchRequest, fail, navigate, orderId, org_path_name]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  

  const handleToggleKitReady = useCallback(async (testKitId: number, isChecked: boolean) => {
    if (!org_path_name || !orderId) {
      navigate('/404.html');
      return;
    }

    setTogglingKitReadyId(testKitId);
    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(org_path_name)}/orders/${encodeURIComponent(orderId)}/test-kits/${testKitId}/kit-ready`;
      const res = await apiFetchRequest({
        endpoint,
        method: 'PATCH',
        body: { kit_ready: isChecked },
      });

      const { status } = res;
      if (status === 401 || status === 403) {
        navigate('/401.html');
        return;
      }

      if (status !== 200) {
        setTestKitSaveError('Failed to update kit ready status.');
        return;
      }

      setTestKitSuccessMessage('Kit ready status updated successfully.');
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          test_kits: prev.test_kits.map((kit) =>
            kit.test_kit_id === testKitId ? { ...kit, kit_ready: isChecked } : kit
          ),
        };
      });
    } catch (e) {
      setTestKitSaveError('Something went wrong. Please try again.');
    } finally {
      setTogglingKitReadyId(null);
    }
  }, [apiFetchRequest, navigate, orderId, org_path_name, fetchOrderDetails]);

  const handleStartEditingTestKit = useCallback((testKit: TestKitDetails) => {
    setEditingTestKitId(testKit.test_kit_id);
    setSampleIdDraft(testKit.sample_id ?? '');
    setTestKitSaveError(null);
    setTestKitSuccessMessage(null);
  }, []);

  const handleCancelEditingTestKit = useCallback(() => {
    setEditingTestKitId(null);
    setSampleIdDraft('');
    setTestKitSaveError(null);
  }, []);

  const handleSaveTestKitSampleId = useCallback(
    async (testKitId: number) => {
      if (!org_path_name || !orderId) {
        navigate('/404.html');
        return;
      }

      const trimmedSampleId = sampleIdDraft.trim();
      if (!trimmedSampleId) {
        setTestKitSaveError('Sample ID is required.');
        return;
      }

      setSavingTestKitId(testKitId);
      setTestKitSaveError(null);
      setTestKitSuccessMessage(null);

      try {
        const endpoint = `${backendURL}/o/${encodeURIComponent(org_path_name)}/orders/${encodeURIComponent(
          orderId,
        )}/test-kits/${testKitId}`;
        const res = await apiFetchRequest({
          endpoint,
          method: 'PATCH',
          body: { sample_id: trimmedSampleId },
        });

        const { status, body } = res;
        if (status === 401 || status === 403) {
          navigate('/401.html');
          return;
        }

        const errorDetail = typeof body === 'string' ? body : typeof body?.detail === 'string' ? body.detail : null;
        if (status === 422) {
          setTestKitSaveError(errorDetail);
          return;
        }

        if (status !== 200) {
          setTestKitSaveError(errorDetail || 'Unable to save the sample ID. Please try again.');
          return;
        }

        const typedBody = body as UpdateSampleIdResponse;
        const persistedSampleId = typedBody.sample_id;

        setData((current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            test_kits: current.test_kits.map((testKit) =>
              testKit.test_kit_id === testKitId ? { ...testKit, sample_id: persistedSampleId } : testKit,
            ),
          };
        });
        setEditingTestKitId(null);
        setSampleIdDraft('');
        setTestKitSaveError(null);
        setTestKitSuccessMessage(`Sample ID saved for test kit #${testKitId}.`);
      } catch {
        setTestKitSaveError('Something went wrong while saving the sample ID. Please try again.');
      } finally {
        setSavingTestKitId(null);
      }
    },
    [apiFetchRequest, navigate, orderId, org_path_name, sampleIdDraft],
  );

  const renderCombinedSection = (order: OrderDetails, customer: CustomerDetails, address: AddressDetails) => {
    const cityLineParts = [address.city, address.province].filter(Boolean);
    const cityLine = cityLineParts.length
      ? `${cityLineParts.join(', ')}${address.zip ? ` ${address.zip}` : ''}`
      : (address.zip ?? null);
    const addressLines = [address.address1, address.address2, cityLine, address.country].filter(
      (line): line is string => Boolean(line),
    );

    const displayPhone = customer.phone || address.phone || 'N/A';
    const addressString = addressLines.join(', ');

    return (
      <OrderDetailsSectionCard sx={{ gridColumn: { lg: '1 / -1' }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box>
            <Typography sx={{ fontWeight: 600, color: sectionValueColor, mb: 1, fontSize: '1.1rem' }}>
              {customer.full_name}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1, flexWrap: 'wrap' }}>
              {customer.email ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover .copy-btn': { opacity: 1 } }}>
                  <Link href={`mailto:${customer.email}`} sx={{ color: sectionLabelColor, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {customer.email}
                  </Link>
                  <Tooltip title="Copy Email">
                    <IconButton
                      className="copy-btn"
                      size="small"
                      onClick={() => navigator.clipboard.writeText(customer.email || '')}
                      sx={{ p: 0.2, opacity: 0, transition: 'opacity 0.2s', color: sectionLabelColor, '&:hover': { color: colors.primaryBlue } }}
                    >
                      <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}

              {displayPhone !== 'N/A' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover .copy-btn': { opacity: 1 } }}>
                  <Link href={`tel:${displayPhone}`} sx={{ color: sectionLabelColor, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {displayPhone}
                  </Link>
                  <Tooltip title="Copy Phone">
                    <IconButton
                      className="copy-btn"
                      size="small"
                      onClick={() => navigator.clipboard.writeText(displayPhone)}
                      sx={{ p: 0.2, opacity: 0, transition: 'opacity 0.2s', color: sectionLabelColor, '&:hover': { color: colors.primaryBlue } }}
                    >
                      <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Typography sx={{ color: sectionLabelColor }}>N/A</Typography>
              )}
            </Box>

            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5, '&:hover .copy-btn': { opacity: 1 } }}>
              <Typography sx={{ color: sectionLabelColor, wordBreak: 'break-word' }}>
                {addressString}
              </Typography>
              {addressString ? (
                <Tooltip title="Copy Address">
                  <IconButton
                    className="copy-btn"
                    size="small"
                    onClick={() => navigator.clipboard.writeText(addressString)}
                    sx={{ p: 0.2, mt: -0.2, opacity: 0, transition: 'opacity 0.2s', color: sectionLabelColor, '&:hover': { color: colors.primaryBlue } }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
          <Divider sx={{ my: 1, borderColor: theme.custom.pages.frost.authHome.cards.borderColor }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) 1fr 1fr', alignItems: 'center', columnGap: 3, pt: 1 }}>
            <Typography sx={{ color: sectionValueColor, fontSize: '1.1rem', fontWeight: 500 }}>
              {order.product_name || 'Test Kit'}
            </Typography>
            <Typography sx={{ color: sectionLabelColor, fontSize: '1.05rem', textAlign: 'right' }}>
              ${order.total_paid ? (Number(order.total_paid) / (order.total_quantity || 1)).toFixed(2) : 'N/A'} <span style={{ padding: '0 4px', fontSize: '0.9rem' }}>x</span> {order.total_quantity}
            </Typography>
            <Typography sx={{ color: sectionValueColor, fontSize: '1.1rem', fontWeight: 700, textAlign: 'right' }}>
              ${order.total_paid ? order.total_paid : 'N/A'}
            </Typography>
          </Box>
        </Box>
      </OrderDetailsSectionCard>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: theme.layout.defaults.backgroundColor, minHeight: '100vh' }}>
      {data && data.order ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ color: theme.custom.components.headerBanner.title.color, fontWeight: 'bold' }}>
            {data.order.name || 'Order Details'}
          </Typography>
          {data.order.fulfillment_status && (
            <StatusPill
              label={data.order.fulfillment_status}
              isActive={data.order.fulfillment_status === 'fulfilled'}
              textColor={sectionValueColor}
            />
          )}
          {data.order.date && (
            <Typography variant="body1" sx={{ color: sectionLabelColor, ml: 1 }}>
              {formatDate(data.order.date)}
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, gap: 2 }}>
          <Typography variant="h4" sx={{ color: theme.custom.components.headerBanner.title.color }}>
            Order Details
          </Typography>
        </Box>
      )}

      {isLoading && !data && !errorText && !notFoundText ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <GradientCircularProgress />
        </Box>
      ) : null}

      {errorText ? (
        <Box sx={{ py: 2 }}>
          <Typography color="error">{errorText}</Typography>
        </Box>
      ) : null}

      {notFoundText ? (
        <Box sx={{ py: 2 }}>
          <Typography>{notFoundText}</Typography>
        </Box>
      ) : null}

      {!errorText && !notFoundText && data ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {renderCombinedSection(data.order, data.customer, data.address)}
          <OrderDetailsOrgTestKitsSection
            testKits={data.test_kits}
            editingTestKitId={editingTestKitId}
            sampleIdDraft={sampleIdDraft}
            savingTestKitId={savingTestKitId}
            togglingKitReadyId={togglingKitReadyId}
            testKitSaveError={testKitSaveError}
            testKitSuccessMessage={testKitSuccessMessage}
            onCloseSuccess={() => setTestKitSuccessMessage(null)}
            onStartEditingTestKit={handleStartEditingTestKit}
            onCancelEditingTestKit={handleCancelEditingTestKit}
            onChangeSampleIdDraft={setSampleIdDraft}
            onSaveTestKitSampleId={handleSaveTestKitSampleId}
            onToggleKitReady={handleToggleKitReady}
          />
        </Box>
      ) : null}
    </Box>
  );
}
